import { Injectable } from "@angular/core";
import { AuditLogInput } from "@app/audit-log/models/audit-log-type.model";
import {
    ADDING_STATUS_STEPS,
    REMOVING_STATUS_STEPS,
    STATUS_STEPS,
    STATUS_STEP_STATUSES,
    ZWAVE_CONSTANTS,
    ZWAVE_STATUS_CODE,
} from "@app/device/services/z-wave/cc-zwave.constants";
import {
    CHZWaveDevice,
    CHZWaveDeviceProperty,
    CHZWaveDeviceResponse,
    CHZWaveDeviceResponseConfigItem,
    CHZWaveDeviceRoutingInfo,
} from "@app/project/model/ch-zwave-device";
import { DeviceDispatchers } from "@app/project/store/dispatchers/device.dispatchers";
import { SnackbarActions } from "@app/root-store/actions";
import { LoadingState } from "@app/root-store/api-result.state";
import { ClareStatusColors, ClareStatuses } from "@app/shared/model/clare-status.model";
import {
    CC_CONFIGURE_ZWAVE_METHODS,
    ClareHomeRPCMethodNames,
    ClareHomeRPCServiceNames,
    DEVICE_CLASSES,
    PROPERTY_MANAGER_HAM_METHODS,
} from "@app/shared/services/equator-api/ClareHomeRpc.constants";
import { EquatorDevicesService } from "@app/shared/services/equator-api/equator-devices";
import { EquatorProxyService } from "@app/shared/services/equator-api/equator-proxy";
import { ConfigurationProp } from "@app/shared/services/zwave-api/ConfigurationProp";
import { Store } from "@ngrx/store";
import { combineLatest, Observable, of } from "rxjs";
import { catchError, delay, filter, map, repeatWhen, switchMap, take, takeWhile, tap } from "rxjs/operators";
import { HttpResponseHandler } from "../http-response-handler";
import { DeviceMetadataParser } from "@app/project/services/device-metadata-parser";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { createAuditLogKey, LOG_CATEGORY, LOG_TYPE, LOG_SUB_TYPE } from "@app/audit-log/services/audit-log.constants";
import { ProjectService } from "@app/project/services/project.service";

@Injectable({
    providedIn: "root",
})
export class ZwaveService {
    constructor(
        private readonly equatorDevicesService: EquatorDevicesService,
        private readonly equatorProxyService: EquatorProxyService,
        private readonly deviceDispatcher: DeviceDispatchers,
        private readonly store: Store<any>,
        private readonly auditLogService: AuditLogService,
        private readonly projectService: ProjectService
    ) {}

    dcoPropertyBlacklist = {
        [DEVICE_CLASSES.INTRUSION_ZONE_CONFIG]: ["zoneName", "entryZone", "signalStrength", "lowBattery"],
        [DEVICE_CLASSES.INTRUSION_PARTITION_CONFIG]: ["centralStationPhoneNumber", "enrollMode", "silentArm"],
    };

    filterBlacklistProperties(metadata) {
        if (!metadata) {
            return [];
        }
        if (!this.dcoPropertyBlacklist[metadata.DeviceClass]) {
            return metadata.Properties;
        }

        return Object.values(metadata.Properties).filter(
            (property: any) =>
                !this.dcoPropertyBlacklist[metadata.DeviceClass].includes(property.Name) && property.Metadata.access.includes("W")
        );
    }

    private call(
        uuid: string,
        method: ClareHomeRPCMethodNames,
        params: any[],
        service: ClareHomeRPCServiceNames = ClareHomeRPCServiceNames.CC_CONFIGURE_ZWAVE
    ) {
        return this.equatorProxyService.makeClareHomeRpcRequest$(uuid, service, method, params);
    }
    public getZwaveTSTATScheduleForPM(uuid) {
        return this.call(uuid, PROPERTY_MANAGER_HAM_METHODS.GET_ZWAVE_SCHEDULE_FOR_PM, [], ClareHomeRPCServiceNames.PROPERTY_MANAGER_HAM);
    }

    public createZwaveTSTATScheduleForPM(uuid, coolSetPoint, heatSetPoint) {
        return this.call(
            uuid,
            PROPERTY_MANAGER_HAM_METHODS.CREATE_ZWAVE_SCHEDULE_FOR_PM,
            ["18:00:00", heatSetPoint, coolSetPoint],
            ClareHomeRPCServiceNames.PROPERTY_MANAGER_HAM
        ).pipe(
            tap(result => {
                this.store.dispatch(
                    SnackbarActions.openSnackbar({
                        message: new HttpResponseHandler(null, null, "Z-Wave thermostat setpoints successfully saved."),
                    })
                );
            })
        );
    }

    public deleteZwaveTSTATScheduleForPM(uuid) {
        return this.call(
            uuid,
            PROPERTY_MANAGER_HAM_METHODS.DELETE_ZWAVE_SCHEDULE_FOR_PM,
            [],
            ClareHomeRPCServiceNames.PROPERTY_MANAGER_HAM
        ).pipe(
            tap(result => {
                this.store.dispatch(
                    SnackbarActions.openSnackbar({
                        message: new HttpResponseHandler(null, null, "Z-Wave thermostat schedule successfully cleared."),
                    })
                );
            })
        );
    }

    public getMode(uuid: string) {
        return this.call(uuid, CC_CONFIGURE_ZWAVE_METHODS.GET_CURRENT_MODE, []);
    }

    public setMode(uuid: string, mode: ZWAVE_STATUS_CODE) {
        return this.call(uuid, CC_CONFIGURE_ZWAVE_METHODS.SET_CURRENT_MODE, [mode]);
    }

    public getController(uuid: string) {
        return this.equatorDevicesService.getFilteredDevices(uuid, "(node_id=1)");
    }

    public getCurrentStatus(uuid: string) {
        return this.call(uuid, CC_CONFIGURE_ZWAVE_METHODS.GET_CURRENT_STATUS, []);
    }

    private prepInputs(inputs: any[]) {
        return inputs.reduce((obj, input, currentIndex) => {
            obj[`IN${currentIndex + 1}`] = input;
            return obj;
        }, {});
    }

    public invokeControllerOperation(uuid: string, uid: string, method: string, params: any[]) {
        const dco = ZWAVE_CONSTANTS.ZWAVE_CONTROLLER_DEVICE_CLASS;
        return this.equatorDevicesService.invokeDCOOperation(uuid, uid, dco, method, this.prepInputs(params));
    }

    public createAssociation(uuid: string, uid: string, sourceNodeId: string, targetNodeId: string, isSecure: boolean) {
        return this.invokeControllerOperation(uuid, uid, "createAssociation", [
            sourceNodeId,
            ZWAVE_CONSTANTS.ZWAVE_ASSOCIATION_GROUP,
            targetNodeId,
            isSecure,
        ]);
    }

    public deleteAssociation(uuid: string, uid: string, sourceNodeId: string, targetNodeId: string, isSecure: boolean) {
        return this.invokeControllerOperation(uuid, uid, "deleteAssociation", [
            sourceNodeId,
            ZWAVE_CONSTANTS.ZWAVE_ASSOCIATION_GROUP,
            targetNodeId,
            isSecure,
        ]);
    }

    public getAssociation(uuid: string, uid: string, sourceNodeId: string, isSecure: boolean) {
        return this.invokeControllerOperation(uuid, uid, "listAssociation", [
            parseInt(sourceNodeId, 10),
            ZWAVE_CONSTANTS.ZWAVE_ASSOCIATION_GROUP,
            isSecure,
        ]);
    }

    public getDeviceConfiguration(uuid: string, uid: string, configurationProps: string) {
        return this.call(uuid, CC_CONFIGURE_ZWAVE_METHODS.GET_ZWAVE_DEVICE_CONFIGURATION, [uid, configurationProps]);
    }

    public setDeviceConfiguration(uuid: string, uid: string, configurationProp: ConfigurationProp) {
        return this.call(uuid, CC_CONFIGURE_ZWAVE_METHODS.SET_ZWAVE_DEVICE_CONFIGURATION, [
            uid,
            parseInt(configurationProp.key, 10),
            parseInt(configurationProp.value, 10),
            configurationProp.bytes,
        ]);
    }

    public isFailedNode(uuid: string, uid: string) {
        return this.call(uuid, CC_CONFIGURE_ZWAVE_METHODS.CHECK_FOR_FAILED_DEVICE, [uid]).pipe(map(response => response.value));
    }

    public getZwaveNodeIds(uuid: string) {
        return this.call(uuid, CC_CONFIGURE_ZWAVE_METHODS.GET_ZWAVE_NODE_IDS, []).pipe(map(response => response));
    }

    public quickConnectivityTest(uuid: string, uid: string) {
        return this.call(uuid, CC_CONFIGURE_ZWAVE_METHODS.QUICK_CONNECTIVITY_TEST, [uid]).pipe(
            map(response => {
                if (response.value === -1) {
                    throw new Error(response?.zwave?.error?.params);
                }
                return response;
            })
        );
    }

    public isFailedZwaveNode(uuid: string, nodeId: string, uid) {
        return this.equatorDevicesService.invokeDCOOperation(
            uuid,
            uid,
            ZWAVE_CONSTANTS.ZWAVE_CONTROLLER_DEVICE_CLASS,
            CC_CONFIGURE_ZWAVE_METHODS.IS_FAILED_NODE,
            { IN1: nodeId } // eslint-disable-line @typescript-eslint/naming-convention
        );
    }

    public removeHDMDevice(uuid: string, uid: string, logInput?: AuditLogInput) {
        return this.equatorDevicesService.remove(uuid, uid, logInput);
    }

    public removeFailedNode(uuid: string, nodeId: string, uid: string, logInput?: AuditLogInput) {
        return this.equatorDevicesService.invokeDCOOperation(
            uuid,
            uid,
            ZWAVE_CONSTANTS.ZWAVE_CONTROLLER_DEVICE_CLASS,
            CC_CONFIGURE_ZWAVE_METHODS.REMOVE_FAILED_NODE,
            { IN1: nodeId }, // eslint-disable-line @typescript-eslint/naming-convention
            logInput
        );
    }

    public removeNode(uuid: string, nodeId: string, forceSleepingNode: boolean) {
        return this.call(uuid, CC_CONFIGURE_ZWAVE_METHODS.REMOVE_NODE, [nodeId, forceSleepingNode]).pipe(map(response => response));
    }

    public setMaintenanceMode(uuid: string, uid: string) {
        return this.equatorDevicesService.setDCOProperty(uuid, uid, ZWAVE_CONSTANTS.ZWAVE_CONTROLLER_DEVICE_CLASS, "mode", "21");
    }

    public getDeviceConfigParamValue(uuid: string, uid: string, operation: any) {
        return this.equatorDevicesService
            .invokeDCOOperation(uuid, uid, operation.DeviceClass, operation.Name, {
                IN1: operation.Metadata.IN1, // eslint-disable-line @typescript-eslint/naming-convention
            })
            .pipe(
                take(1),
                map(result => this.convertFromSignedValue(operation.Metadata, result))
            );
    }

    public setDeviceConfigParamValue(uuid: string, uid: string, operation: any, values: { [key: string]: any }) {
        return this.equatorDevicesService.invokeDCOOperation(
            uuid,
            uid,
            operation.DeviceClass,
            operation.Name,
            this.convertToSignedValue(operation.Metadata, values)
        );
    }

    public attemptDeviceConnectivity(uuid: string, uid: string, values: { [key: string]: any } = null) {
        return this.quickConnectivityTest(uuid, uid);
    }

    public convertToSignedValue(metadata, values) {
        if (metadata.signed && values.IN3 > 127) {
            values.IN3 -= 256;
        }

        if (parseInt(values.IN3, 2) === values.IN3) {
            values.IN3 += "";
        }

        return values;
    }

    public convertFromSignedValue(metadata, value) {
        if (metadata.signed && value < 0) {
            return 256 + value;
        }

        return value;
    }

    public getConnectionStatistics(uuid: string) {
        return this.call(uuid, CC_CONFIGURE_ZWAVE_METHODS.GET_CONNECTION_STATISTICS, []);
    }

    public resetConnectionStatistics(uuid: string, nodeId: string) {
        return this.call(uuid, CC_CONFIGURE_ZWAVE_METHODS.RESET_CONNECTION_STATISTICS, [nodeId]);
    }

    public setModeAndStartPollingForCurrentStatus(uuid: string, mode: ZWAVE_STATUS_CODE) {
        let zwaveCode;
        return this.setMode(uuid, mode).pipe(
            switchMap(result =>
                this.getCurrentStatus(uuid).pipe(
                    map(status => {
                        const obj = JSON.parse(status);
                        zwaveCode = obj["zwave.controller.status.code"];
                        return obj;
                    })
                )
            ),
            repeatWhen(status =>
                status.pipe(
                    delay(2000),
                    takeWhile(() => zwaveCode === 100 || zwaveCode === 110 || zwaveCode === 200 || zwaveCode === 210)
                )
            )
        );
    }

    setStatusMessage(status: any, deviceToRemove?: string) {
        const obj = {
            message: undefined,
            status: STATUS_STEP_STATUSES.LOADING,
            statusCode: status["zwave.controller.status.code"],
            statusDescription: status["zwave.controller.status.descr"],
            errorCode: status["zwave.controller.error.code"],
            errorDescription: status["zwave.controller.error.descr"],
            statusStep: undefined,
            childUids: [],
            uid: status["zwave.controller.status.device.uid"],
        };

        // gets the uids of all children from status config
        let configObj;
        let configs;
        const uids = [];
        if (status && status["config"]) {
            configObj = JSON.parse(status["config"]);
        }
        if (configObj && configObj["config"]) {
            configs = configObj["config"];
            configs.forEach(config => {
                obj.childUids.push(config.UID);
            });
        }

        if (obj.statusCode === ZWAVE_STATUS_CODE.STATUS_REMOVING_DEVICE_FAILURE && !deviceToRemove) {
            // This is not an error. The user was attempting to unpair a device connected to another controller.
            obj.statusCode = ZWAVE_STATUS_CODE.STATUS_REMOVING_DEVICE_SUCCESS;
        }

        return this.finalizeStatusObj(obj, deviceToRemove);
    }

    finalizeStatusObj(obj, deviceToRemove?) {
        switch (obj.statusCode) {
            case ZWAVE_STATUS_CODE.STATUS_ADDING_MODE:
                obj.message = {
                    title: ADDING_STATUS_STEPS.SEARCHING,
                    action: ADDING_STATUS_STEPS.SEARCHING_ACTION,
                    recovery: ADDING_STATUS_STEPS.SEARCHING_RECOVERY,
                };
                obj.statusStep = STATUS_STEPS.SEARCHING;
                break;
            case ZWAVE_STATUS_CODE.STATUS_ADDING_NEW_DEVICE:
                obj.message = {
                    title: ADDING_STATUS_STEPS.PROCESSING,
                    action: ADDING_STATUS_STEPS.PROCESSING_ACTION,
                    recovery: ADDING_STATUS_STEPS.PROCESSING_RECOVERY,
                };
                obj.statusStep = STATUS_STEPS.PROCESSING;
                break;
            case ZWAVE_STATUS_CODE.STATUS_ADDING_DEVICE_SUCCESS:
                obj.status = STATUS_STEP_STATUSES.PASS;
                obj.message = {
                    title: ADDING_STATUS_STEPS.COMPLETE,
                    action: ADDING_STATUS_STEPS.COMPLETE_ACTION,
                };
                obj.statusStep = STATUS_STEPS.COMPLETE;
                break;
            case ZWAVE_STATUS_CODE.STATUS_ADDING_DEVICE_FAILURE:
            case ZWAVE_STATUS_CODE.ERROR_CONTROLLER_ADD_MODE_TIMEOUT:
                obj.status = STATUS_STEP_STATUSES.FAIL;
                break;
            case ZWAVE_STATUS_CODE.STATUS_REMOVING_MODE:
                obj.message = {
                    title: REMOVING_STATUS_STEPS.SEARCHING,
                    action: REMOVING_STATUS_STEPS.SEARCHING_ACTION,
                    recovery: REMOVING_STATUS_STEPS.SEARCHING_RECOVERY,
                };
                break;
            case ZWAVE_STATUS_CODE.STATUS_REMOVING_DEVICE:
                obj.message = {
                    title: REMOVING_STATUS_STEPS.PROCESSING,
                    action: REMOVING_STATUS_STEPS.PROCESSING_ACTION,
                    recovery: REMOVING_STATUS_STEPS.PROCESSING_RECOVERY,
                };
                break;

            // This case is the one
            case ZWAVE_STATUS_CODE.STATUS_REMOVING_DEVICE_SUCCESS:
                if (deviceToRemove && deviceToRemove !== obj.uid) {
                    obj.status = STATUS_STEP_STATUSES.FAIL;
                    obj.message = {
                        recovery: REMOVING_STATUS_STEPS.COMPLETE_WITH_ERROR,
                    };
                } else {
                    obj.status = STATUS_STEP_STATUSES.PASS;
                    obj.message = {
                        title: REMOVING_STATUS_STEPS.COMPLETE,
                    };
                }
                break;
            case ZWAVE_STATUS_CODE.STATUS_REMOVING_DEVICE_FAILURE:
            case ZWAVE_STATUS_CODE.ERROR_CONTROLLER_REMOVE_MODE_TIMEOUT:
                obj.status = STATUS_STEP_STATUSES.FAIL;
                break;
            default:
                obj.status = STATUS_STEP_STATUSES.FAIL;
                break;
        }

        return obj;
    }

    isMaintenanceMode() {
        return this.deviceDispatcher
            .getUuid()
            .pipe(switchMap(uuid => this.getMode(uuid).pipe(switchMap(mode => of(mode === ZWAVE_STATUS_CODE.MODE_MAINTENANCE)))));
    }

    setRemovalMode() {
        return this.deviceDispatcher.getUuid().pipe(
            filter(uuid => !!uuid),
            switchMap(uuid => this.setModeAndStartPollingForCurrentStatus(uuid, ZWAVE_STATUS_CODE.MODE_REMOVING)),
            catchError(e => {
                console.log(e);
                return of(e);
            })
        );
    }

    setPairingMode() {
        return this.deviceDispatcher.getUuid().pipe(
            filter(uuid => !!uuid),
            switchMap(uuid => this.setModeAndStartPollingForCurrentStatus(uuid, ZWAVE_STATUS_CODE.MODE_ADDING)),
            catchError(e => {
                console.log(e);
                return of(e);
            })
        );
    }

    setNormalMode() {
        return this.deviceDispatcher.getUuid().pipe(
            filter(uuid => !!uuid),
            switchMap(uuid => this.setModeAndStartPollingForCurrentStatus(uuid, ZWAVE_STATUS_CODE.MODE_NORMAL)),
            catchError(e => {
                console.log(e);
                return of(e);
            })
        );
    }

    public getUIZWaveDevices(): Observable<CHZWaveDevice[]> {
        return this.deviceDispatcher.selectZWaveDevicesState().pipe(
            map(res => {
                if (res.callState === LoadingState.loaded && res.result) {
                    return Object.values(res.result);
                }
            })
        );
    }

    public getZWaveDevices(uuid: string): Observable<CHZWaveDeviceResponse> {
        return this.call(uuid, CC_CONFIGURE_ZWAVE_METHODS.GET_ZWAVE_DEVICES, []).pipe(
            map((result: string) => JSON.parse(result) as CHZWaveDeviceResponse)
        );
    }

    public getZWaveHops(uuid: string): Observable<CHZWaveDeviceRoutingInfo[]> {
        return this.call(uuid, CC_CONFIGURE_ZWAVE_METHODS.GET_ROUTING_INFO, []) as Observable<CHZWaveDeviceRoutingInfo[]>;
    }

    public getUIZWaveDeviceFromConfigItem(configItem: CHZWaveDeviceResponseConfigItem, hops: CHZWaveDeviceRoutingInfo[]): CHZWaveDevice {
        const properties = configItem.config.find(item => item.name === "Properties").property;
        const networkInfo = configItem["zwave.device.network.info"];
        const nodeId = this.getZWaveProperty("node_id", properties) || null;

        return {
            UID: configItem.UID,
            name: configItem.name || "UNKNOWN",
            onlineStatus:
                configItem.status === 2
                    ? [ClareStatuses.Online, ClareStatusColors.Online]
                    : [ClareStatuses.Offline, ClareStatusColors.Offline],
            nodeId,
            sentMessages: networkInfo["mbs.zwave.network.load.devices.sent"] >= 0 ? networkInfo["mbs.zwave.network.load.devices.sent"] : 0,
            receivedMessages:
                networkInfo["mbs.zwave.network.load.devices.received"] >= 0 ? networkInfo["mbs.zwave.network.load.devices.received"] : 0,
            failedMessages:
                networkInfo["mbs.zwave.network.load.devices.failed"] >= 0 ? networkInfo["mbs.zwave.network.load.devices.failed"] : 0,
            manufacturerId: this.getZWaveProperty("zwave.manufacturer_specific.manufacturer_id", properties) || null,
            productId: this.getZWaveProperty("zwave.manufacturer_specific.product_id", properties) || null,
            typeId: this.getZWaveProperty("zwave.manufacturer_specific.product_type_id", properties) || null,
            hop: hops.find(hop => hop.node_id === 1).hops[nodeId] || 0,
        };
    }

    /**
     * @description finds devices based on uids passed and creates log entry for each one
     * @param {string[]} uids the uids of the devices added
     * @returns {void}
     */
    public logZWaveDevicesAdd(uids: string[]): void {
        combineLatest([
            this.deviceDispatcher.selectDevices(),
            this.deviceDispatcher.selectDeviceMetadata(),
            this.deviceDispatcher.selectServices(),
            this.projectService.getActiveProject$(),
        ])
            .pipe(
                filter(([devices, metadata, services, project]) => !!devices && !!metadata && !!services && !!project),
                take(1),
                map(([devices, metadata, services, project]) => {
                    DeviceMetadataParser.setPathToDevicePropertyInfo(metadata, devices);
                    uids.forEach(uid => {
                        const device = Object.values(devices).find(d => d.UID === uid);
                        const service = Object.values(services).find(s => s.derivedServiceDevice === uid);
                        if (device && service) {
                            const deviceType = this.deviceDispatcher.getDeviceType({ [device.UID]: device }, service);
                            this.auditLogService.logEvent(createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.DEVICES, LOG_SUB_TYPE.ADD), {
                                data: `Device Type: ${deviceType}, Name: ${device.Name}`,
                                entityId: project.entityId,
                                projectId: project.id,
                                optionalParams: { uuid: project.claresystem?.uuid },
                            });
                        }
                    });
                })
            )
            .subscribe();
    }

    private getZWaveProperty(key: string, properties: CHZWaveDeviceProperty[]): string {
        return properties.find(p => p.key === key)?.value;
    }
}
